import type { Coordinate } from '../core/types/SimulationTypes';

export type MouseButton = 'left' | 'right' | 'middle';

export interface MouseState {
  position: Coordinate;
  isDragging: boolean;
  button: MouseButton | null;
}

export class MouseController {
  private state: MouseState = {
    position: { x: 0, y: 0 },
    isDragging: false,
    button: null
  };

  private listeners: {
    onMouseDown?: (state: MouseState, event: MouseEvent) => void;
    onMouseMove?: (state: MouseState, event: MouseEvent) => void;
    onMouseUp?: (state: MouseState, event: MouseEvent) => void;
  } = {};

  constructor(private element: HTMLElement) {
    this.element.addEventListener('mousedown', this.handleMouseDown);
    this.element.addEventListener('mousemove', this.handleMouseMove);
    this.element.addEventListener('mouseup', this.handleMouseUp);
    this.element.addEventListener('mouseleave', this.handleMouseLeave);
    this.element.addEventListener('contextmenu', this.handleContextMenu);
  }

  private getMouseButton(event: MouseEvent): MouseButton {
    switch (event.button) {
      case 0: return 'left';
      case 1: return 'middle';
      case 2: return 'right';
      default: return 'left';
    }
  }

  private handleMouseDown = (event: MouseEvent): void => {
    this.state.isDragging = true;
    this.state.button = this.getMouseButton(event);
    this.state.position = {
      x: event.clientX,
      y: event.clientY
    };

    if (this.listeners.onMouseDown) {
      this.listeners.onMouseDown(this.state, event);
    }
  };

  private handleMouseMove = (event: MouseEvent): void => {
    this.state.position = {
      x: event.clientX,
      y: event.clientY
    };

    if (this.listeners.onMouseMove) {
      this.listeners.onMouseMove(this.state, event);
    }
  };

  private handleMouseUp = (event: MouseEvent): void => {
    this.state.isDragging = false;
    this.state.button = null;

    if (this.listeners.onMouseUp) {
      this.listeners.onMouseUp(this.state, event);
    }
  };

  private handleMouseLeave = (): void => {
    this.state.isDragging = false;
    this.state.button = null;
  };

  private handleContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  public on(
    event: 'mousedown' | 'mousemove' | 'mouseup',
    callback: (state: MouseState, event: MouseEvent) => void
  ): void {
    if (event === 'mousedown') {
      this.listeners.onMouseDown = callback;
    } else if (event === 'mousemove') {
      this.listeners.onMouseMove = callback;
    } else if (event === 'mouseup') {
      this.listeners.onMouseUp = callback;
    }
  }

  public getState(): MouseState {
    return { ...this.state };
  }

  public dispose(): void {
    this.element.removeEventListener('mousedown', this.handleMouseDown);
    this.element.removeEventListener('mousemove', this.handleMouseMove);
    this.element.removeEventListener('mouseup', this.handleMouseUp);
    this.element.removeEventListener('mouseleave', this.handleMouseLeave);
    this.element.removeEventListener('contextmenu', this.handleContextMenu);
  }
}
